import Component from "@base/Component.js";
import config from "@/config.js";
import { lockScroll, unlockScroll } from "@/services/scrollLock.js";
import t from "@/services/translationService.js";

/**
 * Admin overlay for moving photos from one gallery into another. Mount to
 * document.body. props: { galleries() -> GalleryObject[], lang, sourceId?,
 * onMove(sourceId, targetId, names) -> Promise, onMoved?() -> Promise }
 *
 * `galleries` is a function, not an array: a move changes both galleries' image
 * lists, and the overlay stays open afterwards — so it has to read whatever the
 * manager holds now, not the snapshot it was opened with.
 *
 * A context of its own rather than a control inside GalleryEditor, for two
 * reasons. One: the editor is a form about *one* gallery, and everything in it
 * is staged until Save (an image crossed out there is still on disk until the
 * save reconciles) — a move is immediate and touches a second gallery, so
 * sitting it among staged edits would make Cancel mean two different things.
 * Two: moving photos is triage, not editing. You do it to a run of images at
 * once, repeatedly, which wants both galleries on screen together and the
 * overlay staying open between moves.
 *
 * Both galleries are picked here, so this opens from the Gallery Manager's
 * header without a gallery having to be open first; `sourceId` just preselects
 * the left-hand one.
 */
export default class MoveImagesModal extends Component {
  constructor(props) {
    super(props);
    const galleries = this.#all();
    const preferred = galleries.find((g) => g.galleryId === props.sourceId);
    this.sourceId = (preferred || galleries.find((g) => this.#images(g).length) || galleries[0])?.galleryId || "";
    this.targetId = "";
    this.selected = new Set();
    this.status = "";
  }

  #all() {
    return this.props.galleries?.() || [];
  }

  #images(gallery) {
    return (gallery?.images || []).map((i) => i.name);
  }

  #gallery(id) {
    return this.#all().find((g) => g.galleryId === id) || null;
  }

  /** "Rome 2024 — Public · 24" — enough to tell two shoots of the same trip apart. */
  #optionLabel(gallery) {
    const visibility = gallery.isPublic()
      ? t.t("cockpit.visibility.public")
      : gallery.isDraft()
      ? t.t("cockpit.visibility.draft")
      : t.t("cockpit.visibility.private");
    const title = gallery.title(this.props.lang) || gallery.galleryId;
    return `${title} — ${visibility} · ${this.#images(gallery).length}`;
  }

  /**
   * `excludeId` greys out one entry rather than hiding it: the gallery you are
   * moving out of is exactly the one you look for first in the target list, and
   * a missing row reads as a bug where a dimmed one reads as the answer.
   */
  #options(selectedId, { placeholder = "", excludeId = null } = {}) {
    const val = (v) => Component.escape(v);
    const opts = this.#all()
      .map(
        (g) =>
          `<option value="${val(g.galleryId)}" ${g.galleryId === selectedId ? "selected" : ""} ${
            g.galleryId === excludeId ? "disabled" : ""
          }>${val(this.#optionLabel(g))}</option>`
      )
      .join("");
    return placeholder
      ? `<option value="" ${selectedId ? "" : "selected"}>${val(placeholder)}</option>${opts}`
      : opts;
  }

  render() {
    const val = (v) => Component.escape(v);
    const source = this.#gallery(this.sourceId);
    const target = this.#gallery(this.targetId);
    const names = this.#images(source);

    const thumbs = names
      .map(
        (name) => `
        <button type="button" class="move-thumb${this.selected.has(name) ? " move-thumb--picked" : ""}"
                data-name="${val(name)}" aria-pressed="${this.selected.has(name) ? "true" : "false"}">
          <img src="${config.imageBase}/${encodeURIComponent(this.sourceId)}/${encodeURIComponent(
          name
        )}?size=thumb" alt="" loading="lazy" />
          <span class="move-thumb__mark" aria-hidden="true">✓</span>
        </button>`
      )
      .join("");

    // Where they land, so a mis-picked target shows itself before the move
    // rather than after it. A strip, not a second grid: it is a confirmation,
    // not something to act on.
    const targetNames = this.#images(target).slice(0, 10);
    const targetStrip = target
      ? targetNames.length
        ? `<div class="move-strip">${targetNames
            .map(
              (name) =>
                `<img class="move-strip__img" src="${config.imageBase}/${encodeURIComponent(
                  this.targetId
                )}/${encodeURIComponent(name)}?size=thumb" alt="" loading="lazy" />`
            )
            .join("")}</div>`
        : `<p class="move-panel__note">${val(t.t("cockpit.galleries.move.targetEmpty"))}</p>`
      : `<p class="move-panel__note">${val(t.t("cockpit.galleries.move.targetHint"))}</p>`;

    // Moving out of a draft or private gallery into a public one publishes the
    // photos — the one consequence of this dialog that isn't undone by moving
    // them back, so it is said before the move, not after.
    const goesPublic = Boolean(source && target && target.isPublic() && !source.isPublic());

    return `
      <div class="modal-backdrop">
        <div class="modal-card modal-card--wide move-modal" role="dialog" aria-modal="true"
             aria-label="${val(t.t("cockpit.galleries.move.title"))}">
          <div class="modal-header">
            <h2>${val(t.t("cockpit.galleries.move.title"))}</h2>
            <button type="button" class="modal-close" aria-label="${val(t.t("common.close"))}">&times;</button>
          </div>
          <p class="move-modal__lead">${val(t.t("cockpit.galleries.move.lead"))}</p>

          <div class="move-cols">
            <section class="move-panel">
              <label class="move-panel__pick">${val(t.t("cockpit.galleries.move.from"))}
                <select name="source">${this.#options(this.sourceId)}</select>
              </label>
              <div class="move-panel__bar">
                <span class="move-count" role="status">${val(this.#countLabel(names.length))}</span>
                <span class="move-panel__bar-actions">
                  <button type="button" class="move-link" data-action="all">${val(
                    t.t("cockpit.galleries.move.selectAll")
                  )}</button>
                  <button type="button" class="move-link" data-action="none">${val(
                    t.t("cockpit.galleries.move.clear")
                  )}</button>
                </span>
              </div>
              ${
                names.length
                  ? `<div class="move-grid">${thumbs}</div>`
                  : `<p class="move-panel__note">${val(t.t("cockpit.galleries.move.sourceEmpty"))}</p>`
              }
            </section>

            <section class="move-panel">
              <label class="move-panel__pick">${val(t.t("cockpit.galleries.move.to"))}
                <select name="target">${this.#options(this.targetId, {
                  placeholder: t.t("cockpit.galleries.move.choose"),
                  excludeId: this.sourceId,
                })}</select>
              </label>
              <div class="move-panel__bar">
                <span class="move-panel__bar-label">${val(t.t("cockpit.galleries.move.alreadyThere"))}</span>
              </div>
              ${targetStrip}
              ${goesPublic ? `<p class="move-warning">${val(t.t("cockpit.galleries.move.warnPublic"))}</p>` : ""}
            </section>
          </div>

          <p class="move-status" role="status">${val(this.status)}</p>
          <div class="form-error" role="alert"></div>
          <div class="editor-actions">
            <button type="button" class="btn-primary" data-action="move" disabled>${val(
              this.#submitLabel()
            )}</button>
            <button type="button" class="btn-secondary" data-action="close">${val(t.t("common.close"))}</button>
          </div>
        </div>
      </div>`;
  }

  #countLabel(total) {
    return t.t("cockpit.galleries.move.selected").replace("{count}", this.selected.size).replace("{total}", total);
  }

  #submitLabel() {
    return this.selected.size === 1
      ? t.t("cockpit.galleries.move.submitOne")
      : t.t("cockpit.galleries.move.submit").replace("{count}", this.selected.size);
  }

  onMounted() {
    lockScroll();

    const close = () => this.unmount();
    this.listen(this.$(".modal-close"), "click", close);
    this.listen(this.$('[data-action="close"]'), "click", close);
    this.listen(this.el, "click", (e) => e.target === this.el && close());
    this.listen(document, "keydown", (e) => e.key === "Escape" && close());

    // Changing either gallery re-renders: the grid, the target strip and the
    // public-visibility warning all follow from the pair.
    this.listen(this.$('select[name="source"]'), "change", (e) => {
      this.sourceId = e.target.value;
      // Picking the current target as the new source would leave both sides on
      // one gallery; drop the target rather than silently disabling the move.
      if (this.targetId === this.sourceId) this.targetId = "";
      this.selected.clear();
      this.status = "";
      this.setState({});
    });
    this.listen(this.$('select[name="target"]'), "change", (e) => {
      this.targetId = e.target.value;
      this.status = "";
      this.setState({});
    });

    // Selection is a targeted DOM patch rather than setState(), for the same
    // reason as GalleryEditor's thumbnails: a re-render restarts the card's
    // scroll position, which in a gallery of 200 photos throws you back to the
    // top on every click.
    this.$$(".move-thumb").forEach((btn) =>
      this.listen(btn, "click", () => {
        const name = btn.getAttribute("data-name");
        if (this.selected.has(name)) this.selected.delete(name);
        else this.selected.add(name);
        this.#reflectSelection();
      })
    );

    this.listen(this.$('[data-action="all"]'), "click", () => {
      this.#images(this.#gallery(this.sourceId)).forEach((name) => this.selected.add(name));
      this.#reflectSelection();
    });
    this.listen(this.$('[data-action="none"]'), "click", () => {
      this.selected.clear();
      this.#reflectSelection();
    });

    this.listen(this.$('[data-action="move"]'), "click", () => this.#move());

    this.#reflectSelection();
  }

  #reflectSelection() {
    for (const btn of this.$$(".move-thumb")) {
      const picked = this.selected.has(btn.getAttribute("data-name"));
      btn.classList.toggle("move-thumb--picked", picked);
      btn.setAttribute("aria-pressed", picked ? "true" : "false");
    }
    const count = this.$(".move-count");
    if (count) count.textContent = this.#countLabel(this.#images(this.#gallery(this.sourceId)).length);
    const submit = this.$('[data-action="move"]');
    if (submit) {
      submit.textContent = this.#submitLabel();
      submit.disabled = !this.selected.size || !this.targetId || this.targetId === this.sourceId;
    }
  }

  async #move() {
    const errorEl = this.$(".form-error");
    const submit = this.$('[data-action="move"]');
    const target = this.#gallery(this.targetId);
    const names = [...this.selected];
    errorEl.textContent = "";
    submit.disabled = true;
    submit.textContent = t.t("cockpit.galleries.move.moving");

    try {
      const moved = await this.props.onMove(this.sourceId, this.targetId, names);
      // The manager's galleries are refreshed by the caller, so re-rendering
      // picks up both new image lists rather than this overlay keeping its own.
      await this.props.onMoved?.();

      const renamed = (moved || []).filter((m) => m.from !== m.to).length;
      this.status =
        t
          .t("cockpit.galleries.move.done")
          .replace("{count}", (moved || []).length)
          .replace("{gallery}", target?.title(this.props.lang) || this.targetId) +
        (renamed ? ` ${t.t("cockpit.galleries.move.renamed").replace("{count}", renamed)}` : "");
      this.selected.clear();
      // Stays open on purpose: sorting a shoot means several moves in a row.
      this.setState({});
    } catch (err) {
      errorEl.textContent = err.message || t.t("cockpit.galleries.move.errMove");
      this.#reflectSelection();
    }
  }

  onBeforeUnmount() {
    unlockScroll();
  }
}
